<template>
  <v-container class="posts-list">
    <v-row justify="center">
      <v-col cols="12">
        <ViewHeader :title="viewTitle">
          <div class="posts-list__header-actions">
            <div class="d-flex align-center mr-3">
              <v-tooltip bottom :max-width="500">
                <template v-slot:activator="{ on }">
                  <div v-on="on">
                    <v-icon color="lime" size="18">
                      mdi-information
                    </v-icon>
                  </div>
                </template>
                {{ $t("message.posts.list.titleTooltip") }}
              </v-tooltip>
              <VideoButton
                class="ml-3"
                @click="showVideoTutorial(viewEnum.POSTS)"
              />

              <PostsGuideButton class="ml-4" />
            </div>
            <div
              v-if="canAddAndDeleteLocalPosts"
              class="posts-list__manage-actions"
            >
              <ui-button
                v-if="showFbCopyButton"
                color="primary"
                outlined
                icon="mdi-content-copy"
                @click="toggleFbPostsDialog"
              >
                {{ $t("message.posts.copyFacebookPost") }}
              </ui-button>
              <ui-button
                :to="{ name: 'posts-add' }"
                color="primary"
                icon="icon-plus"
              >
                {{ $t("message.common.addNew") }}
              </ui-button>
            </div>
          </div>
        </ViewHeader>
        <v-card>
          <v-tabs v-model="tab" slider-color="primary" class="tabs px-1">
            <v-tab
              :ripple="false"
              :to="{ name: 'posts-list-sent' }"
              class="tabs__item"
            >
              {{ $t("message.posts.sent") }}
            </v-tab>
            <v-tab
              v-if="canPlanPosts"
              :ripple="false"
              :to="{ name: 'posts-list-planned' }"
              class="tabs__item"
            >
              {{ $t("message.posts.list.planned") }}
            </v-tab>
          </v-tabs>
          <v-tabs-items
            class="tabs__content"
            v-model="tab"
            @change="updateRouter($event)"
          >
            <keep-alive>
              <router-view> </router-view>
            </keep-alive>
          </v-tabs-items>
        </v-card>
      </v-col>
    </v-row>
    <FbPostsDialog
      v-if="showFbPostsDialog"
      @close="showFbPostsDialog = false"
    />
  </v-container>
</template>

<script>
import ViewHeader from "@/components/ui/ViewHeader.vue";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";
import { gtmEngagement as gtmEngagementMixin } from "@/helpers/mixins/gtm-engagement";
import PostsGuideButton from "@/views/posts/PostsGuideButton";
import { dateRangeFilterMixin } from "@/helpers/mixins/date-range-filter";
import { videoTutorialsMixin } from "@/helpers/mixins/video-tutorials";
import VideoButton from "@/components/ui/VideoButton.vue";

import facebookService from "@/services/facebook";
import serviceHandler from "@/helpers/handlers/service";

import { PLAN_RESTRICTIONS } from "@/enums/plan-restrictions";
import { planRestrictionsCheck } from "@/helpers/plan-restrictions-check";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";

export default {
  name: "PostsList",
  mixins: [
    permissionsInfo,
    gtmEngagementMixin,
    dateRangeFilterMixin,
    videoTutorialsMixin
  ],
  components: {
    ViewHeader,
    PostsGuideButton,
    VideoButton,
    FbPostsDialog: () => import("@/components/Posts/dialog/FbPostsDialog.vue")
  },
  data() {
    return {
      viewTitle: this.$t("message.common.postsList"),
      tab: null,
      showFbPostsDialog: false,
      hasFbAccounts: false
    };
  },
  computed: {
    showFbCopyButton() {
      return (
        this.hasFbAccounts &&
        isFeatureFlagEnabled(FEATURE_FLAG.CN_6637_COPY_FACEBOOK_POSTS)
      );
    },
    canPlanPosts() {
      return planRestrictionsCheck(
        PLAN_RESTRICTIONS.CAN_PLAN_MARKETING_PUBLICATIONS
      );
    }
  },
  async created() {
    await this.getFbAccountsList();
  },
  methods: {
    async getFbAccountsList() {
      const data = await serviceHandler(facebookService.getAccounts());
      this.hasFbAccounts = data.length > 0;
    },
    toggleFbPostsDialog() {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.POST_COPY_FROM_FACEBOOK_STARTED
      });
      this.showFbPostsDialog = true;
    },
    updateRouter(val) {
      this.$router.push(val);
    }
  }
};
</script>

<style lang="scss" scoped>
.tabs {
  &__item {
    min-width: 280px;
  }
}
::v-deep .posts-list {
  &__wrapper {
    padding: $spacing * 3.5;
  }

  &__header-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1;
    margin-left: $spacing * 1.75;
  }

  &__manage-actions {
    display: flex;
    gap: $spacing * 1.5;
  }

  &__list-wrapper {
    position: relative;
    margin: 0;
  }

  &__item {
    &:not(:last-child) {
      border-bottom: 1px solid $light-3;
    }
  }
  &__search-field {
    max-width: 645px;
    margin-right: auto;
  }

  &__deleted-toggle {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: $spacing;
    margin: 0 $spacing * 2;
  }
}

.min-h {
  min-height: 650px;
}

.min-w-230 {
  min-width: 230px;
}
</style>
